import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import "../scss/index.scss";
import { Helmet } from "react-helmet";
import { autoLoginUser } from "../store/actions/user.actions";
import usePrivyAuth from "../hooks/usePrivyAuth";
import useEmailOtpLogin from "../hooks/useEmailOtpLogin";
import AuthLoader from "../components/AuthLoader";
import TurnstileWidget from "../components/TurnstileWidget";
import { TURNSTILE_SITE_KEY } from "../config/privyConfig";
import partnersLogo from "../images/ISPartnersLogo.png";
import { captureInviteToken } from "../components/globalView/AgencyBanners";
import { ppapiPost } from "../utils/partnerApi";

const CodeInput = ({ value, onChange, onEnter, disabled }) => (
  <input
    className="input mono"
    type="text"
    inputMode="numeric"
    autoComplete="one-time-code"
    maxLength={6}
    placeholder="123456"
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
    onKeyDown={(e) => {
      if (e.key === "Enter" && value.length === 6 && !disabled && onEnter) onEnter();
    }}
    style={{ textAlign: "center", marginBottom: 12 }}
  />
);

const Login = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userReducer.user);
  const authenticated = useSelector((state) => state.userReducer.authenticated);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    ready,
    authenticated: privyAuthenticated,
    privyUser,
    isProcessing,
    error,
    chooser,
    twoFactorPrompt,
    noAccount,
    login,
    signIn,
    register,
    chooseAccount,
    submit2FA,
    cancel,
    privyLogout,
  } = usePrivyAuth();
  const { requestEmailLoginOtp, confirmEmailLoginOtp } = useEmailOtpLogin();

  const [twoFaCode, setTwoFaCode] = React.useState("");
  const [twoFaError, setTwoFaError] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [registerError, setRegisterError] = React.useState("");
  // Turnstile clearance for the create-account step (null until the visitor is cleared).
  const [turnstileToken, setTurnstileToken] = React.useState(null);
  const [turnstileReset, setTurnstileReset] = React.useState(0);
  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY);
  const canCreate = Boolean(companyName.trim()) && !isProcessing && (!turnstileRequired || Boolean(turnstileToken));

  // Fallback: our own email code. 'hidden' | 'email' | 'code' | 'twofactor'
  const [otpStep, setOtpStep] = React.useState("hidden");
  const [otpBusy, setOtpBusy] = React.useState(false);
  const [otpEmail, setOtpEmail] = React.useState("");
  const [otpCode, setOtpCode] = React.useState("");
  const [otpTwoFa, setOtpTwoFa] = React.useState("");
  const [otpError, setOtpError] = React.useState("");
  const [otpNotice, setOtpNotice] = React.useState("");

  useEffect(() => {
    document.body.classList.add("isw-login");
    return () => document.body.classList.remove("isw-login");
  }, []);

  useEffect(() => {
    if (authenticated) navigate("/");
    // eslint-disable-next-line
  }, [user, authenticated, navigate]);

  // SSO handoff from the trade app / website, and admin-minted links.
  useEffect(() => {
    const ssid = new URLSearchParams(location.search).get("ssid");
    if (ssid && ssid.trim()) dispatch(autoLoginUser(ssid));
  }, [location, dispatch]);

  // Agency invite link (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3 C): keep the token for
  // registration (new account → KOL already linked) or the post-login banner.
  const [inviteAgency, setInviteAgency] = React.useState(null);
  useEffect(() => {
    const token = captureInviteToken(location.search);
    if (!token) return;
    ppapiPost("inviteInfo", { token }, { view: false })
      .then((d) => setInviteAgency(d?.valid === true && d?.agencyName ? d.agencyName : d?.valid === false ? false : null))
      .catch(() => setInviteAgency(null));
  }, [location.search]);

  const privyEmail =
    privyUser?.email?.address || privyUser?.google?.email || (noAccount?.emails || [])[0] || "";

  // ---- fallback email OTP -------------------------------------------------
  const otpRequest = async () => {
    setOtpError("");
    setOtpNotice("");
    setOtpBusy(true);
    const r = await requestEmailLoginOtp(otpEmail.trim().toLowerCase());
    setOtpBusy(false);
    if (r.ok) {
      setOtpStep("code");
      setOtpNotice("We sent a 6-digit code to your email. It expires in 10 minutes.");
    } else {
      setOtpError(r.error);
    }
  };

  const otpConfirm = async () => {
    setOtpError("");
    setOtpBusy(true);
    const r = await confirmEmailLoginOtp(
      otpEmail.trim().toLowerCase(),
      otpCode.trim(),
      otpStep === "twofactor" ? otpTwoFa.trim() : undefined
    );
    setOtpBusy(false);
    if (r.ok) return;
    if (r.twoFactor) {
      setOtpStep("twofactor");
      setOtpNotice("Enter the code from your authenticator app to finish signing in.");
      return;
    }
    setOtpError(r.error);
  };

  const otpReset = () => {
    setOtpStep("hidden");
    setOtpCode("");
    setOtpTwoFa("");
    setOtpError("");
    setOtpNotice("");
  };

  const handleRegister = async () => {
    if (!canCreate) return;
    setRegisterError("");
    const r = await register(companyName.trim(), turnstileToken);
    if (!r.ok) {
      if (r.error) setRegisterError(r.error);
      // A Turnstile token is single-use: ask for a fresh challenge before the next try.
      setTurnstileToken(null);
      setTurnstileReset((n) => n + 1);
    }
    // Account mode (Partner / KOL / Agency) is asked once inside the dashboard (AccountTypeOnboardingModal).
  };

  const startOver = async () => {
    cancel();
    setCompanyName("");
    setRegisterError("");
    setTurnstileToken(null);
    try {
      await privyLogout();
    } catch (e) {
      /* ignore */
    }
  };

  const showLoader = isProcessing && !chooser && !twoFactorPrompt && !noAccount;

  return (
    <>
      <Helmet>
        <title>Log In | Instaswap Partners</title>
      </Helmet>

      <div className="axStage">
        <div className="axBg" />
        <div className="axVeil" />

        <div className="axBrand">
          <img src={partnersLogo} alt="InstaSwap Partners" style={{ height: 52, width: "auto" }} />
        </div>

        <div className="axWrap">
          <div className="axL">
            <div className="axKick">Partner desk</div>
            <h1 className="axH">Access your partner dashboard.</h1>
            <p className="axLead">
              Sign in with Google, your email or your wallet to manage revenue,
              referrals, and API access. Same account as the trade app and
              instaswap.com.
            </p>
            <div className="axList">
              <div className="axItem"><span className="d" /><span className="t">Affiliate revenue, paid on-chain</span></div>
              <div className="axItem"><span className="d" /><span className="t">Referral analytics in real time</span></div>
              <div className="axItem"><span className="d" /><span className="t">Full API and documentation access</span></div>
            </div>
          </div>

          <div className="axR">
            <AuthLoader isVisible={showLoader} />

            {!showLoader && (
              <div className="axModal">
                <h2>Sign in</h2>

                {inviteAgency && (
                  <div className="notice green" style={{ marginBottom: 14 }}>
                    <span className="ni">✓</span>
                    <div>
                      <b>{inviteAgency}</b> invited you as a KOL. Sign in or create your account below — the agency will see your
                      campaign stats read-only; you keep full control and can revoke any time.
                    </div>
                  </div>
                )}
                {inviteAgency === false && (
                  <div className="notice red" style={{ marginBottom: 14 }}>
                    <span className="ni">⚠</span>
                    <div>This invite link is no longer valid. You can still sign in or create a normal account.</div>
                  </div>
                )}

                {otpStep === "hidden" && (
                  <>
                    <p>
                      One account across InstaSwap. If you signed in with Google or
                      a wallet on the trade app before, use the same one here.
                    </p>
                    <button
                      type="button"
                      className="btnPrimary block"
                      onClick={() => (privyAuthenticated ? signIn() : login())}
                      disabled={!ready || isProcessing}
                    >
                      {!ready ? "Loading…" : privyAuthenticated ? "Continue" : "Sign in / Create account"}
                    </button>
                    {privyAuthenticated && (
                      <button type="button" className="cancel" onClick={startOver}>
                        Use a different account
                      </button>
                    )}
                    {error && (
                      <p style={{ color: "#e5484d", fontSize: 13, marginTop: 10, marginBottom: 0 }}>{error}</p>
                    )}
                    <div className="div" style={{ marginTop: 18 }}>
                      <button
                        type="button"
                        className="cancel"
                        onClick={() => { setOtpStep("email"); setOtpEmail(privyEmail || ""); }}
                      >
                        Trouble signing in? Email me a one-time code
                      </button>
                    </div>
                  </>
                )}

                {otpStep === "email" && (
                  <>
                    <p>Enter your partner account email and we&apos;ll send you a 6-digit sign-in code.</p>
                    <input
                      className="input"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && otpEmail.trim() && !otpBusy) otpRequest(); }}
                      style={{ marginBottom: 12 }}
                    />
                    <button type="button" className="btnPrimary block" onClick={otpRequest} disabled={otpBusy || !otpEmail.trim()}>
                      {otpBusy ? "Sending code…" : "Email me a sign-in code"}
                    </button>
                    <button type="button" className="cancel" onClick={otpReset}>Back</button>
                  </>
                )}

                {otpStep === "code" && (
                  <>
                    <p>Enter the 6-digit code we sent to <strong>{otpEmail.trim().toLowerCase()}</strong>.</p>
                    <CodeInput value={otpCode} onChange={setOtpCode} onEnter={otpConfirm} disabled={otpBusy} />
                    <button type="button" className="btnPrimary block" onClick={otpConfirm} disabled={otpBusy || otpCode.length !== 6}>
                      {otpBusy ? "Verifying…" : "Verify & sign in"}
                    </button>
                    <button type="button" className="cancel" onClick={otpRequest}>Send a new code</button>
                    <button type="button" className="cancel" onClick={otpReset}>Back</button>
                  </>
                )}

                {otpStep === "twofactor" && (
                  <>
                    <p>Enter the 6-digit code from your authenticator app to finish signing in.</p>
                    <CodeInput value={otpTwoFa} onChange={setOtpTwoFa} onEnter={otpConfirm} disabled={otpBusy} />
                    <button type="button" className="btnPrimary block" onClick={otpConfirm} disabled={otpBusy || otpTwoFa.length !== 6}>
                      {otpBusy ? "Verifying…" : "Verify & sign in"}
                    </button>
                    <button type="button" className="cancel" onClick={otpReset}>Start over</button>
                  </>
                )}

                {otpStep !== "hidden" && otpNotice && (
                  <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 10, marginBottom: 0 }}>{otpNotice}</p>
                )}
                {otpStep !== "hidden" && otpError && (
                  <p style={{ color: "#e5484d", fontSize: 13, marginTop: 10, marginBottom: 0 }}>{otpError}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {chooser && (
          <div className="axModalOverlay">
            <div className="axModal">
              <h2>Which account?</h2>
              <p>
                Your sign-in matches more than one partner account. Pick the one
                to open — the others stay exactly as they are.
              </p>
              {chooser.map((c) => (
                <button
                  key={c.partnerId}
                  type="button"
                  className="btnGhost"
                  style={{ width: "100%", textAlign: "left", marginBottom: 8 }}
                  disabled={isProcessing}
                  onClick={() => chooseAccount(c.partnerId)}
                >
                  <div style={{ fontWeight: 600 }}>{c.name || "Partner account"}</div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>
                    {[c.email, c.wallet].filter(Boolean).join(" · ")}
                    {c.via?.length ? ` · matched by ${c.via.join(", ")}` : ""}
                  </div>
                </button>
              ))}
              <button type="button" className="cancel" onClick={startOver}>Cancel</button>
            </div>
          </div>
        )}

        {noAccount && (
          <div className="axModalOverlay">
            <div className="axModal">
              <h2>Create your partner account</h2>
              <p>
                No partner account is linked to this sign-in yet
                {privyEmail ? <> (<strong>{privyEmail}</strong>)</> : null}. Give it a
                name and you&apos;re in.
              </p>
              <input
                className="input"
                type="text"
                autoComplete="organization"
                placeholder="Company or project name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canCreate) handleRegister(); }}
                style={{ marginBottom: 12 }}
              />

              {turnstileRequired && (
                <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetKey={turnstileReset} />
              )}

              <button type="button" className="btnPrimary block" onClick={handleRegister} disabled={!canCreate}>
                {isProcessing ? "Creating…" : "Create account"}
              </button>
              {registerError && (
                <p style={{ color: "#e5484d", fontSize: 13, marginTop: 10 }}>{registerError}</p>
              )}
              <div className="div" style={{ marginTop: 18 }}>
                <p style={{ fontSize: 12.5, opacity: 0.75 }}>
                  Already a partner under a different email or wallet? Sign in with
                  that one instead, then link this login from Linked Wallets.
                </p>
                <button type="button" className="cancel" onClick={startOver}>Use a different account</button>
                <button
                  type="button"
                  className="cancel"
                  onClick={() => { cancel(); setOtpStep("email"); setOtpEmail(""); }}
                >
                  Sign in with an email code instead
                </button>
              </div>
            </div>
          </div>
        )}

        {twoFactorPrompt && (
          <div className="axModalOverlay">
            <div className="axModal">
              <h2>Two-factor authentication</h2>
              <p>Enter the 6-digit code from your authenticator app to finish signing in.</p>
              <CodeInput
                value={twoFaCode}
                onChange={(v) => { setTwoFaCode(v); if (twoFaError) setTwoFaError(""); }}
                disabled={isProcessing}
                onEnter={async () => {
                  const ok = await submit2FA(twoFaCode);
                  if (ok) setTwoFaCode(""); else setTwoFaError("Invalid 2FA code. Please try again.");
                }}
              />
              {twoFaError && (
                <div className="notice red" style={{ marginBottom: 14 }}>
                  <span className="ni">⚠</span>
                  <div>{twoFaError}</div>
                </div>
              )}
              <button
                type="button"
                className="btnPrimary block"
                disabled={isProcessing || twoFaCode.length !== 6}
                onClick={async () => {
                  const ok = await submit2FA(twoFaCode);
                  if (ok) setTwoFaCode(""); else setTwoFaError("Invalid 2FA code. Please try again.");
                }}
              >
                Verify &amp; sign in
              </button>
              <button type="button" className="cancel" onClick={() => { setTwoFaCode(""); setTwoFaError(""); startOver(); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Login;
